import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Session, User as SupabaseUser } from '@supabase/supabase-js';
// import { errorTracking } from '@/lib/error-tracking';
// import { monitoring } from '@/lib/monitoring';

export interface AuthUser {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  role: 'admin' | 'tenant_admin' | 'user';
  job_title?: string | null;
  tenant_id?: string | null;
  user_type?: 'internal' | 'external' | 'supplier' | 'admin';
  created_at: string;
}

interface AuthContextType {
  user: AuthUser | null;
  session: Session | null;
  loading: boolean;
  setUser: (user: AuthUser | null) => void;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, displayName?: string, redirectTo?: string) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  isAdmin: boolean;
  isTenantAdmin: boolean;
  // Workspace modal state for direct signups
  showWorkspaceModal: boolean;
  closeWorkspaceModal: () => void;
  openWorkspaceModal: () => void;
  refreshWorkspaceData: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // Workspace modal state for direct signups
  const [showWorkspaceModal, setShowWorkspaceModal] = useState(false);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          setSession(session);
          try {
            await loadUserProfile(session.user);
            // Track successful authentication
          } catch (error) {
            // If profile loading fails, clear the session completely
            await supabase.auth.signOut();
            setSession(null);
            setUser(null);
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      } catch (error) {
        await supabase.auth.signOut();
        setSession(null);
        setUser(null);
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const loadUserProfile = async (supabaseUser: SupabaseUser) => {
    try {
      setLoading(true);

      // WORKING APPROACH: Create user object directly from auth.users data
      
      const authUser: AuthUser = {
        id: supabaseUser.id,
        email: supabaseUser.email || '',
        display_name: supabaseUser.user_metadata?.display_name || supabaseUser.email?.split('@')[0] || 'User',
        avatar_url: supabaseUser.user_metadata?.avatar_url || null,
        role: 'user',
        created_at: supabaseUser.created_at
      };

      setUser(authUser);
      setLoading(false);

      // Background check: Ensure user exists in watt_users (don't block auth flow)
      ensureUserInWattUsers(supabaseUser).catch(error => {
        // Don't throw - this is a background operation
      });

      // Check if user needs workspace modal (for direct signups only)
      checkWorkspaceNeeds(authUser);
      
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  // Background function to ensure user exists in watt_users
  const ensureUserInWattUsers = async (supabaseUser: SupabaseUser) => {
    try {
      // Check if user exists in watt_users
      const { data: existingUser, error: checkError } = await supabase
        .from('watt_users')
        .select('id')
        .eq('id', supabaseUser.id)
        .maybeSingle();

      if (checkError) {
        return;
      }

      if (existingUser) {
        return;
      }

      // User doesn't exist, try to create them
      const { error: insertError } = await supabase
        .from('watt_users')
        .insert({
          id: supabaseUser.id,
          email: supabaseUser.email,
          display_name: supabaseUser.user_metadata?.display_name || supabaseUser.email?.split('@')[0] || 'User',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (insertError) {
        // Don't throw - this is background operation
      } else {
      }
    } catch (error) {
    }
  };

  // Check if user needs workspace modal (for direct signups only)
  const checkWorkspaceNeeds = async (authUser: AuthUser) => {
    try {
      
      // Skip workspace check if on invitation routes
      if (typeof window !== 'undefined' && (
        window.location.pathname.startsWith('/invite/') ||
        window.location.search.includes('invitationId=')
      )) {
        return;
      }

      // Check if user has any workspace memberships
      try {
        const { data: memberships, error: membershipError } = await (supabase as any)
          .from('workspace_memberships')
          .select('id')
          .eq('user_id', authUser.id);

        if (membershipError) {
          // If we can't check memberships, assume user needs workspace (safer for new users)
          setShowWorkspaceModal(true);
          return;
        }

        const hasWorkspaces = memberships && memberships.length > 0;
        
        if (hasWorkspaces) {
          return;
        }

        // Check if user previously dismissed the modal
        const dismissedKey = `wattfactory-workspace-onboarding-dismissed-${authUser.id}`;
        const wasDismissed = localStorage.getItem(dismissedKey);
        
        if (!wasDismissed) {
          setShowWorkspaceModal(true);
        }
        
      } catch (membershipCheckError) {
        // If we can't check memberships, assume user needs workspace (safer for new users)
        setShowWorkspaceModal(true);
      }
      
    } catch (error) {
      setShowWorkspaceModal(true);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      // Use regular Supabase auth for all sign-ins
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        return { error };
      }
      
      if (data.session && data.user) {
        setSession(data.session);
        await loadUserProfile(data.user);
      }
      
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const signUp = async (email: string, password: string, displayName?: string, redirectTo?: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName || email.split('@')[0]
          },
          emailRedirectTo: redirectTo
        }
      });
      
      return { error };
    } catch (error) {
      return { error };
    }
  };

  const signOut = async () => {
    try {
      // Sign out from Supabase (this will clear sessionStorage auth data)
      const { error } = await supabase.auth.signOut();
      
      setUser(null);
      setSession(null);
      setShowWorkspaceModal(false);
      
      // Clear any session data that might cause redirects
      sessionStorage.removeItem('intendedPlan');
      sessionStorage.clear(); // Clear all session storage
      
      // Clear only workspace-related localStorage (keep other app data)
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('wattfactory-workspace-onboarding-dismissed-')) {
          localStorage.removeItem(key);
        }
      });
      
      // Redirect to home page to ensure clean state
      window.location.href = '/';
      
      return { error };
    } catch (error) {
      return { error };
    }
  };

  const closeWorkspaceModal = () => {
    setShowWorkspaceModal(false);
    
    // Mark as dismissed for this user
    if (user) {
      const dismissedKey = `wattfactory-workspace-onboarding-dismissed-${user.id}`;
      localStorage.setItem(dismissedKey, 'true');
    }
  };

  const openWorkspaceModal = () => {
    setShowWorkspaceModal(true);
  };

  const refreshWorkspaceData = async () => {
    
    // Force re-check of workspace needs
    if (user) {
      await checkWorkspaceNeeds(user);
    }
    
    // Note: Individual components using useDefaultWorkspace will need to call their own refresh
    // This is intentional to avoid circular dependencies
  };

  // Computed properties
  const isAdmin = user?.role === 'admin';
  const isTenantAdmin = user?.role === 'admin' || user?.role === 'tenant_admin';

  const value: AuthContextType = {
    user,
    session,
    loading,
    setUser,
    signIn,
    signUp,
    signOut,
    isAdmin,
    isTenantAdmin,
    showWorkspaceModal,
    closeWorkspaceModal,
    openWorkspaceModal,
    refreshWorkspaceData
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useIsAuthenticated = (): boolean => {
  const { user } = useAuth();
  return !!user;
};

export const useIsAdmin = (): boolean => {
  const { isAdmin } = useAuth();
  return isAdmin;
};

export const useIsTenantAdmin = (): boolean => {
  const { isTenantAdmin } = useAuth();
  return isTenantAdmin;
};

export const useCurrentUser = (): AuthUser | null => {
  const { user } = useAuth();
  return user;
}; 